import styles from '@components/components/CmsComponents/components/CmsCustomerWizard/CmsCustomerWizard.module.scss';
import { useTracking } from '@components/hooks';
import { Button as BaseButton } from '@ariakit/react';
import {
  Alert,
  Button,
  Card,
  CardBody,
  ChoiceCard,
  Content,
  Heading,
  Link,
} from '@wienenergiegithub/ui-next';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';

const getTrackingShape = (answers, event, step) => ({
  event,
  steps: step,
  answers: answers.map(answer => answer.param),
});

const AnswersCard = ({ answers, setAnswers, step, setStep, track }) => {
  const handleClick = () => {
    const shape = getTrackingShape(answers, 'ChoiceCardEvent_reset', step);
    if (answers.length > 0) {
      track(shape);
    }
    setStep(0);
    setAnswers([]);
  };

  return (
    <Card className={styles.answerCard}>
      <CardBody>
        <div className={styles.answerHeader}>
          <Heading className={styles.title} noSpacing size="h6">
            Ihre Auswahl
          </Heading>
          <Button
            className={styles.button}
            onClick={handleClick}
            variant="link"
          >
            zurücksetzen
          </Button>
        </div>
        <div>
          {answers?.map(({ text, label, param }) => (
            <div key={param}>
              {param && (
                <div className={styles.answer} key={label}>
                  <Content className={styles.label}>{label}:</Content>
                  <Content className={styles.label}>{text}</Content>
                </div>
              )}
            </div>
          ))}
        </div>
      </CardBody>
    </Card>
  );
};

const FinalStep = ({
  alertText,
  alertTitle,
  answers,
  setAnswers,
  links,
  step,
  setStep,
  track,
}) => {
  const param = answers.map(answer => answer.param).join('-');
  const link = links.find(item => item.name === param);

  useEffect(() => {
    const shape = getTrackingShape(answers, 'ChoiceCardEvent', step);

    if (answers.length > 0) {
      track(shape);
    }
  }, [answers]);

  const handleClick = () => {
    const shape = getTrackingShape(answers, 'ChoiceCardEvent_send', step);
    track(shape);
  };

  return (
    <div>
      <div className={styles.finalStep}>
        <Alert
          details={alertText}
          fontAwesomeIcon="info"
          isElevated
          title={alertTitle}
          type="info"
        />
        {answers.length !== 0 && (
          <AnswersCard
            answers={answers}
            setAnswers={setAnswers}
            setStep={setStep}
            step={step}
            track={track}
          />
        )}
      </div>
      {link && (
        <Button
          onClick={handleClick}
          render={<Link target="_blank" to={link?.link.url} />}
        >
          Jetzt Gratis-Tage in Meine Wien Energie einlösen
        </Button>
      )}
    </div>
  );
};

const QuestionsComponent = ({
  title,
  subtitle,
  label: optionLabel,
  options,
  step,
  setStep,
  setAnswers,
  answers,
  track,
}) => {
  useEffect(() => {
    const shape = getTrackingShape(answers, 'ChoiceCardEvent', step);
    if (answers.length > 0) {
      track(shape);
    }
  }, [answers]);

  const handleClick = (text, param, label) => {
    if (param === 'gas') {
      setStep(prev => prev + 2);
      setAnswers([...answers, { text, param, label }, { param: '' }]);
    } else {
      setStep(prev => prev + 1);
      setAnswers([...answers, { text, param, label }]);
    }
  };

  return (
    <div className={classNames(styles.questions)}>
      <Heading className={styles.title} size="h2">
        {title}
      </Heading>
      <Content className={styles.subtitle} withParser>
        {subtitle}
      </Content>
      <div className={styles.optionsWrapper}>
        {options?.map(({ text, image, links, param }) => (
          <ChoiceCard
            as={BaseButton}
            icon={image}
            image={image}
            key={text}
            links={links}
            onClick={() => handleClick(text, param, optionLabel)}
            title={text}
          />
        ))}
      </div>
      {answers.length !== 0 && (
        <AnswersCard
          answers={answers}
          setAnswers={setAnswers}
          setStep={setStep}
          step={step}
          track={track}
        />
      )}
    </div>
  );
};

const CustomerWizard = ({ alertText, alertTitle, items, links }) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const track = useTracking();

  const questionsToRender = items?.map((item, index) => ({
    ...item,
    Component: QuestionsComponent,
    order: index,
  }));
  const stepToRender = [
    ...questionsToRender,
    {
      Component: FinalStep,
      order: items.length,
    },
  ];

  const renderSteps = () => {
    const [CurrentStepComponent] = stepToRender.filter(
      component => component.order === step,
    );

    return CurrentStepComponent ? (
      <CurrentStepComponent.Component
        alertText={alertText}
        alertTitle={alertTitle}
        answers={answers}
        links={links}
        setAnswers={setAnswers}
        setStep={setStep}
        step={step}
        {...items[step]}
        track={track}
      />
    ) : null;
  };

  return renderSteps(step);
};

export default CustomerWizard;
